/**
 * Native-style page/route transitions.
 *
 * `resolvePageMotion` is a pure function that maps a navigation
 * (prev route → next route, plus the app's ordered tab groups) to one of five
 * transition variants, mirroring native navigation grammar:
 *
 *   - push      deeper into a stack (e.g. /catalog → /catalog/:id):
 *               the detail slides in from the trailing edge.
 *   - pop       back out of a stack: the page returns from the leading edge.
 *   - forward   lateral tab change to a higher-ordered tab.
 *   - backward  lateral tab change to a lower-ordered tab.
 *   - fade      anything else (unknown relation): fade-through.
 *
 * `MotionOutlet` renders the routed content inside a solid-transition-group
 * <Transition mode="outin"> (Material fade-through timing: quick exit, springy
 * enter) keyed by the route, using the resolved variant's CSS classes — all
 * transform/opacity only (see motion.css). The global prefers-reduced-motion
 * guard collapses the durations to ~0.
 */

import { createMemo, type JSX, Show } from "solid-js";
import { Transition } from "solid-transition-group";

type PageMotionVariant = "push" | "pop" | "forward" | "backward" | "fade";

/** Ordered tab groups: each entry lists the route prefixes owned by that tab. */
export type TabGroups = readonly (readonly string[])[];

function depthOf(path: string): number {
  return path.split("/").filter(Boolean).length;
}

function tabIndexOf(path: string, tabs: TabGroups): number {
  if (path === "/") {
    return tabs.findIndex((group) => group.includes("/"));
  }
  return tabs.findIndex((group) =>
    group.some((prefix) => prefix !== "/" && (path === prefix || path.startsWith(`${prefix}/`))),
  );
}

/** Pure variant resolver — unit-tested in __tests__/page-motion.test.ts. */
export function resolvePageMotion(
  prev: string | null,
  next: string,
  tabs: TabGroups,
): PageMotionVariant {
  if (prev === null || prev === next) {
    return "fade";
  }

  const prevDepth = depthOf(prev);
  const nextDepth = depthOf(next);
  const sameStack =
    prev !== "/" && next !== "/" && (next.startsWith(`${prev}/`) || prev.startsWith(`${next}/`));

  // Lateral tab change FIRST: if both routes belong to (different) tabs of the
  // bottom bar, the direction follows the tab order — including Home → tab.
  const prevTab = tabIndexOf(prev, tabs);
  const nextTab = tabIndexOf(next, tabs);
  if (prevTab !== -1 && nextTab !== -1 && prevTab !== nextTab && !sameStack) {
    return nextTab > prevTab ? "forward" : "backward";
  }

  // Stack navigation: deeper = push, shallower = pop. Home acts as the root of
  // every non-tab stack (e.g. / → /scanner is a push, /scanner → / a pop).
  if (nextDepth > prevDepth && (sameStack || prev === "/")) {
    return "push";
  }
  if (nextDepth < prevDepth && (sameStack || next === "/")) {
    return "pop";
  }

  // Same tab, sibling depth change without a shared stack (rare) — treat depth
  // growth as push so detail-like routes still feel stacked.
  if (nextDepth > prevDepth) {
    return "push";
  }
  if (nextDepth < prevDepth) {
    return "pop";
  }

  return "fade";
}

interface MotionOutletProps {
  /** Normalised route key (account prefix stripped), e.g. "/catalog/abc". */
  routeKey: string;
  /** Ordered tab groups of the bottom bar (drives lateral direction). */
  tabs: TabGroups;
  children: JSX.Element;
  class?: string;
}

/**
 * Route-content wrapper with directional native transitions. The wrapper div
 * remounts per `routeKey`; solid-transition-group cross-fades old → new using
 * the variant classes (`motion-page-<variant>-{enter,exit}-*`).
 */
export function MotionOutlet(props: MotionOutletProps) {
  let prevKey: string | null = null;

  const variant = createMemo<PageMotionVariant>(() => {
    const next = props.routeKey;
    const resolved = resolvePageMotion(prevKey, next, props.tabs);
    prevKey = next;
    return resolved;
  });

  return (
    <Transition name={`motion-page-${variant()}`} mode="outin">
      <Show when={props.routeKey} keyed>
        <div class={props.class} data-motion-page={variant()}>
          {props.children}
        </div>
      </Show>
    </Transition>
  );
}
